import { useEffect, useRef, useState } from 'react';
import { Button } from '@givebutter/primitives/button';
import { faArrowRight, faChevronRight } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import * as RecommendedAction from 'components/recommended-actions/RecommendedAction';
import { IRecommendedActionContext } from 'components/recommended-actions/RecommendedActionContext';
import {
    RecommendedActionsProvider,
    useRecommendedActionsContext,
} from 'components/recommended-actions/RecommendedActionsContext';
import { RecommendedActionCompleteDialog } from 'components/recommended-actions/dialogs/RecommendedActionCompleteDialog';
import {
    useInvalidateRecommendedActionsQuery,
    useRecommendedActionsData,
} from 'components/recommended-actions/useRecommendedActionsData';
import { useAccountLocalStorage } from 'contexts/useAccountLocalStorage';
import { AnimatePresence, motion } from 'framer-motion';

const RecommendedActions = () => {
    const { invalidateRecommendedActionsQuery } = useInvalidateRecommendedActionsQuery();
    const { showCompletionPopup, visibleActions, containerRef } = useRecommendedActionsContext();
    const { data } = useRecommendedActionsData();
    const {
        seeMoreRecommendedActions: seeMore,
        toggleSeeMoreRecommendedActions: toggleSeeMore,
        hasSeenCompleteAllRecommendedActionsDialog,
    } = useAccountLocalStorage(
        ({
            seeMoreRecommendedActions,
            toggleSeeMoreRecommendedActions,
            hasSeenCompleteAllRecommendedActionsDialog,
        }) => ({
            seeMoreRecommendedActions,
            toggleSeeMoreRecommendedActions,
            hasSeenCompleteAllRecommendedActionsDialog,
        }),
    );

    useEffect(() => {
        invalidateRecommendedActionsQuery();
    }, [invalidateRecommendedActionsQuery]);

    if (
        data.length === 0 ||
        (visibleActions.length === 0 && hasSeenCompleteAllRecommendedActionsDialog)
    ) {
        return null;
    }

    return (
        <div className="flex flex-col gap-6">
            <p className="font-medium">Make the most of Givebutter!</p>
            <ul className="flex flex-col gap-4">
                <div
                    className="flex flex-col gap-4 overflow-hidden sm:max-h-[455px] sm:flex-row"
                    ref={containerRef}
                >
                    <div className="w-full sm:mb-2 sm:max-w-[412px]">
                        <AnimatePresence initial={false} mode="wait">
                            {visibleActions.length > 0 && (
                                <PrebuiltRecommendedAction
                                    key={`hero-action-${visibleActions[0].actionKey}`}
                                    variant="primary"
                                    action={visibleActions[0]}
                                />
                            )}
                        </AnimatePresence>
                    </div>
                    <div className="flex w-full flex-col gap-4 pb-2 sm:min-w-[520px]">
                        <AnimatePresence initial={false} mode="sync">
                            {visibleActions.slice(1, 4).map(action => (
                                <PrebuiltRecommendedAction
                                    key={`action-side-${action.actionKey}`}
                                    variant="secondary"
                                    action={action}
                                />
                            ))}
                        </AnimatePresence>
                    </div>
                </div>
                <AnimatePresence initial={false} mode="sync">
                    {seeMore &&
                        visibleActions
                            .slice(4)
                            .map(action => (
                                <PrebuiltRecommendedAction
                                    key={`action-belowfold-${action.actionKey}`}
                                    variant="secondary"
                                    action={action}
                                />
                            ))}
                </AnimatePresence>
            </ul>
            {visibleActions.length > 4 && (
                <div className="text-center">
                    <Button variant="none" onClick={toggleSeeMore}>
                        {seeMore ? 'See less' : 'See more'}
                    </Button>
                </div>
            )}

            <RecommendedActionCompleteDialog open={showCompletionPopup} />
        </div>
    );
};

const defaultMaxHeight = 138;

interface PrebuiltRecommendedActionProps extends IRecommendedActionContext {}
const PrebuiltRecommendedAction = ({ variant, action }: PrebuiltRecommendedActionProps) => {
    const { onClearAnimation, visibleActions } = useRecommendedActionsContext();
    const [maxHeight, setMaxHeight] = useState(defaultMaxHeight);
    const itemRef = useRef<HTMLDivElement | null>(null);

    useEffect(() => {
        if (itemRef.current) {
            setMaxHeight(itemRef.current.clientHeight);
        }
    }, []);

    if (variant === 'primary') {
        return (
            <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.3 }}
                className="h-full"
                onAnimationComplete={() => {
                    if (visibleActions.length === 0) {
                        onClearAnimation();
                    }
                }}
            >
                <RecommendedAction.Root
                    className="h-full flex-col"
                    variant={variant}
                    action={action}
                >
                    <RecommendedAction.IconContainer className="relative h-56" />
                    <div className="flex flex-col gap-4">
                        <div>
                            <RecommendedAction.Duration className="p-1 pr-2" />
                        </div>
                        <div className="flex flex-col gap-1">
                            <RecommendedAction.Title>
                                <FontAwesomeIcon icon={faArrowRight} size="xs" />
                            </RecommendedAction.Title>
                            <RecommendedAction.Description />
                        </div>
                    </div>
                </RecommendedAction.Root>
            </motion.div>
        );
    } else {
        return (
            <motion.div
                initial={{ opacity: 0, maxHeight }}
                animate={{ opacity: 1, maxHeight, marginBottom: 0 }}
                exit={{ opacity: 0, maxHeight: 0, marginBottom: '-1rem' }}
                transition={{ duration: 0.3 }}
            >
                <div ref={itemRef}>
                    <RecommendedAction.Root
                        className="flex-col items-center p-5 sm:flex-row"
                        variant={variant}
                        action={action}
                    >
                        <RecommendedAction.IconContainer className="relative h-24 shrink-0 sm:size-24" />
                        <div className="flex w-full items-center justify-between gap-3">
                            <div className="flex w-full flex-col gap-1">
                                <div className="flex gap-2">
                                    <RecommendedAction.Title />
                                    <RecommendedAction.Duration />
                                </div>
                                <RecommendedAction.Description />
                            </div>
                            <FontAwesomeIcon icon={faChevronRight} className="p-2 text-gray-500" />
                        </div>
                    </RecommendedAction.Root>
                </div>
            </motion.div>
        );
    }
};

const WrappedRecommendedActions = () => {
    return (
        <RecommendedActionsProvider>
            <RecommendedActions />
        </RecommendedActionsProvider>
    );
};

export { WrappedRecommendedActions as RecommendedActions };
